import { useEffect, useRef } from "react";

import { prefersReducedMotion } from "../lib/motion";
import "./HexLattice.css";

/* ===========================================================================
   The signature element.

   The Intelligence Lab mark is three overlapping hexagons wired together with
   circuit nodes. This component tiles that same geometry across a section and
   lets current run through it: pulses walk the lattice edge by edge, and the
   cursor energises whatever it passes over.

   Canvas 2D rather than WebGL on purpose — the brand is flat vector work, so
   a shader would cost frames without buying any fidelity.
   =========================================================================== */

const PALETTE = {
  light: {
    /* Barely there. The reference background is flat, so the lattice has to
       read as texture you notice second, never as wallpaper. */
    base: "rgba(14, 17, 51, 0.032)",
    hot: [43, 78, 255],
    pulses: ["#f12659", "#1ab6fe", "#fea602"],
    node: [43, 78, 255],
  },
  onBlue: {
    base: "rgba(255, 255, 255, 0.1)",
    hot: [255, 255, 255],
    pulses: ["#ffb31f", "#ffffff", "#1ab6fe"],
    node: [255, 255, 255],
  },
};

/** Vertices of a pointy-top hexagon — the mark's own orientation. */
function hexPoints(cx, cy, r) {
  const pts = [];
  for (let i = 0; i < 6; i += 1) {
    const a = ((60 * i - 90) * Math.PI) / 180;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

/** Tile the plane and collapse the shared hexagon walls into a unique graph. */
function buildLattice(width, height, r) {
  const stepX = Math.sqrt(3) * r;
  const stepY = 1.5 * r;
  const nodes = new Map();
  const edges = [];
  const edgeKeys = new Set();

  const nodeAt = (x, y) => {
    const key = `${Math.round(x)}|${Math.round(y)}`;
    let node = nodes.get(key);
    if (!node) {
      node = { key, x, y, edges: [] };
      nodes.set(key, node);
    }
    return node;
  };

  const rows = Math.ceil(height / stepY) + 2;
  const cols = Math.ceil(width / stepX) + 2;

  for (let row = -1; row < rows; row += 1) {
    for (let col = -1; col < cols; col += 1) {
      const cx = col * stepX + (row % 2 ? stepX / 2 : 0);
      const cy = row * stepY;
      const pts = hexPoints(cx, cy, r);

      for (let i = 0; i < 6; i += 1) {
        const a = nodeAt(pts[i][0], pts[i][1]);
        const b = nodeAt(pts[(i + 1) % 6][0], pts[(i + 1) % 6][1]);
        const key = a.key < b.key ? `${a.key}~${b.key}` : `${b.key}~${a.key}`;
        if (edgeKeys.has(key)) continue;
        edgeKeys.add(key);

        const index = edges.length;
        edges.push({ a, b, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 });
        a.edges.push(index);
        b.edges.push(index);
      }
    }
  }

  return { nodes: [...nodes.values()], edges };
}

export default function HexLattice({ variant = "light", radius = 58, className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d", { alpha: true });
    const theme = PALETTE[variant] ?? PALETTE.light;
    const reduced = prefersReducedMotion();

    let lattice = null;
    let base = null; // offscreen static wall layer
    let raf = 0;
    let visible = true;
    let pulses = [];
    let dpr = 1;
    let cssW = 0;
    let cssH = 0;

    const pointer = { x: -9999, y: -9999, active: false };
    const HOT_RADIUS = 190;

    /* --- setup --------------------------------------------------------- */

    function seedPulses(count) {
      pulses = Array.from({ length: count }, (_, i) => {
        const edge = Math.floor(Math.random() * lattice.edges.length);
        return {
          edge,
          from: lattice.edges[edge].a,
          t: Math.random(),
          speed: 0.0022 + Math.random() * 0.0032,
          color: theme.pulses[i % theme.pulses.length],
        };
      });
    }

    function measure() {
      const rect = canvas.parentElement.getBoundingClientRect();
      cssW = Math.max(1, Math.round(rect.width));
      cssH = Math.max(1, Math.round(rect.height));
      dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);

      // Wider cells on small screens: fewer, larger cells read as pattern
      // rather than noise, and cut the edge count roughly in half.
      const r = cssW < 700 ? radius * 1.22 : radius;
      lattice = buildLattice(cssW, cssH, r);

      // Bake the static walls once; per-frame work is then only the live bits.
      base = document.createElement("canvas");
      base.width = canvas.width;
      base.height = canvas.height;
      const bctx = base.getContext("2d");
      bctx.scale(dpr, dpr);
      bctx.strokeStyle = theme.base;
      bctx.lineWidth = 1;
      bctx.beginPath();
      lattice.edges.forEach(({ a, b }) => {
        bctx.moveTo(a.x, a.y);
        bctx.lineTo(b.x, b.y);
      });
      bctx.stroke();

      seedPulses(cssW < 700 ? 5 : 11);
    }

    /* --- frame --------------------------------------------------------- */

    function drawStatic() {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(base, 0, 0);
    }

    function frame() {
      drawStatic();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const { edges } = lattice;

      /* Cursor halo — bucket the alphas so the whole halo is three strokes. */
      if (pointer.active) {
        const buckets = [[], [], []];
        for (let i = 0; i < edges.length; i += 1) {
          const e = edges[i];
          const dx = e.mx - pointer.x;
          const dy = e.my - pointer.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > HOT_RADIUS * HOT_RADIUS) continue;
          const f = 1 - Math.sqrt(d2) / HOT_RADIUS;
          buckets[Math.min(2, Math.floor(f * 3))].push(e);
        }

        buckets.forEach((bucket, i) => {
          if (!bucket.length) return;
          const [r, g, b] = theme.hot;
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.045 + i * 0.075})`;
          ctx.lineWidth = 1 + i * 0.35;
          ctx.beginPath();
          bucket.forEach(({ a, b: bb }) => {
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(bb.x, bb.y);
          });
          ctx.stroke();
        });

        /* Nodes light up like solder points. */
        const [nr, ng, nb] = theme.node;
        for (let i = 0; i < lattice.nodes.length; i += 1) {
          const n = lattice.nodes[i];
          const dx = n.x - pointer.x;
          const dy = n.y - pointer.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > HOT_RADIUS * HOT_RADIUS) continue;
          const f = 1 - Math.sqrt(d2) / HOT_RADIUS;
          ctx.fillStyle = `rgba(${nr}, ${ng}, ${nb}, ${f * 0.55})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, 1.1 + f * 2.4, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      /* Current running through the lattice. */
      ctx.lineCap = "round";
      for (let i = 0; i < pulses.length; i += 1) {
        const p = pulses[i];
        const edge = edges[p.edge];
        const head = p.from === edge.a ? edge.b : edge.a;
        const tail = p.from;

        const hx = tail.x + (head.x - tail.x) * p.t;
        const hy = tail.y + (head.y - tail.y) * p.t;
        const t2 = Math.max(0, p.t - 0.5);
        const tx = tail.x + (head.x - tail.x) * t2;
        const ty = tail.y + (head.y - tail.y) * t2;

        const grad = ctx.createLinearGradient(tx, ty, hx, hy);
        grad.addColorStop(0, "rgba(0,0,0,0)");
        grad.addColorStop(1, p.color);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.72;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(hx, hy);
        ctx.stroke();

        ctx.globalAlpha = 0.95;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(hx, hy, 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;

        p.t += p.speed;
        if (p.t >= 1) {
          /* Walk on: pick another wall out of the node we just reached,
             preferring not to double back. */
          const options = head.edges.filter((idx) => idx !== p.edge);
          const next = options.length
            ? options[Math.floor(Math.random() * options.length)]
            : p.edge;
          p.edge = next;
          p.from = head;
          p.t = 0;
        }
      }

      raf = requestAnimationFrame(frame);
    }

    /* --- lifecycle ------------------------------------------------------ */

    function start() {
      if (raf || reduced) return;
      raf = requestAnimationFrame(frame);
    }

    function stop() {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    }

    function rebuild() {
      measure();
      if (reduced) drawStatic();
    }

    rebuild();
    if (reduced) {
      // Still show the lattice — just don't animate it.
      return () => {};
    }

    const onPointerMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.active = true;
    };
    const onPointerLeave = () => {
      pointer.active = false;
    };

    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(rebuild, 180);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
        else stop();
      },
      { threshold: 0 },
    );
    io.observe(canvas);

    const host = canvas.parentElement;
    host.addEventListener("pointermove", onPointerMove);
    host.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", onResize);

    if (visible) start();

    return () => {
      stop();
      io.disconnect();
      window.clearTimeout(resizeTimer);
      host.removeEventListener("pointermove", onPointerMove);
      host.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", onResize);
    };
  }, [variant, radius]);

  return <canvas ref={canvasRef} className={`hex-lattice ${className}`} aria-hidden="true" />;
}
